import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { InkField } from './InkField';
import { RevealText } from './RevealText';
import { Wordmark } from './Wordmark';

/**
 * Login / registration / first-run bootstrap page. Three modes:
 *
 *  - bootstrap (no users yet): one form creates the owner/admin account
 *    (forced login `maclaren`) and signs in immediately.
 *  - sign-in: workspace<N> login + password.
 *  - request-access: collect REAL NAME + password only; the server
 *    assigns a workspace<N> login and queues the request. We then show
 *    the assigned login on a "wait for confirmation" screen — no login
 *    happens until the admin approves.
 */

type Mode = 'login' | 'register';

export function LoginPage() {
  const { status, login, register, serverStatus } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [realName, setRealName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('login');
  // Set once a self-service request is accepted — switches the card to
  // the "your login is X, wait for approval" confirmation screen.
  const [assignedLogin, setAssignedLogin] = useState<string | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  // Set right before a sign-in request so success can play the exit
  // animation; an already-authenticated visit redirects at once.
  const signingIn = useRef(false);
  const [leaving, setLeaving] = useState(false);

  const bootstrap = serverStatus?.bootstrap_needed === true;
  const registrationOpen = serverStatus?.registration_open !== false;

  useEffect(() => {
    if (status !== 'authenticated') return;
    if (!signingIn.current) {
      navigate({ to: '/new', replace: true });
      return;
    }
    setLeaving(true);
    const id = setTimeout(() => navigate({ to: '/new', replace: true }), 380);
    return () => clearTimeout(id);
  }, [status, navigate]);

  function shake(): void {
    const el = formRef.current;
    if (!el) return;
    el.classList.remove('mc-shake');
    void el.offsetWidth; // restart the animation on repeated errors
    el.classList.add('mc-shake');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    signingIn.current = true;
    try {
      if (bootstrap) {
        await register(realName.trim() || 'maclaren', password);
      } else if (mode === 'register') {
        const { login: assigned } = await register(realName.trim(), password);
        setAssignedLogin(assigned);
      } else {
        await login(username.trim(), password);
      }
    } catch (err) {
      signingIn.current = false;
      setError(err instanceof Error ? err.message : 'Не удалось выполнить запрос');
      shake();
    } finally {
      setSubmitting(false);
    }
  }

  // ---- Confirmation screen (request submitted) ----
  if (assignedLogin) {
    return (
      <div
        lang="ru"
        className="mc-brand relative flex min-h-full items-center justify-center bg-sidebar px-4"
      >
        <InkField className="absolute inset-0" />
        <div className="relative w-full max-w-[380px]">
          <div className="mb-8 flex items-center justify-center">
            <Wordmark className="text-[30px]" />
          </div>
          <div className="mc-enter rounded-2xl border border-border bg-surface p-6 text-center shadow-xl shadow-[oklch(var(--primary)/0.08)]">
            <h1 className="mb-1 text-[22px] font-semibold">Ждите подтверждения</h1>
            <p className="mb-5 text-[13px] text-muted-foreground">
              Заявка отправлена администратору. Доступ появится после подтверждения.
            </p>
            <div className="mb-5 rounded-lg border border-border bg-background px-4 py-3">
              <span className="block text-[12px] text-muted-foreground">Ваш логин</span>
              <span className="select-all font-mono text-[18px] font-semibold">
                {assignedLogin}
              </span>
            </div>
            <p className="mb-5 text-[12px] leading-relaxed text-muted-foreground">
              Запомните логин — он понадобится для входа вместе с вашим паролем.
            </p>
            <button
              type="button"
              onClick={() => {
                setAssignedLogin(null);
                setMode('login');
                setUsername(assignedLogin);
                setPassword('');
                setRealName('');
              }}
              className="mc-press min-h-11 w-full rounded-lg bg-primary py-2.5 font-medium text-primary-foreground hover:opacity-90"
            >
              Перейти ко входу
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isRegister = !bootstrap && mode === 'register';
  const heading = bootstrap ? 'Создать аккаунт' : isRegister ? 'Запросить доступ' : 'Вход';
  const subcopy = bootstrap
    ? 'Первичная настройка. Этот аккаунт станет администратором.'
    : isRegister
      ? 'Укажите имя и пароль. Логин будет назначен автоматически.'
      : 'У каждого профиля свой логин и изолированное рабочее пространство.';

  return (
    <div
      lang="ru"
      className="mc-brand relative grid min-h-full bg-sidebar lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
    >
      <InkField className="absolute inset-0 lg:right-1/2" />
      {/* Calm the field under the headline so body copy stays readable. */}
      <div className="pointer-events-none absolute inset-0 right-1/2 hidden bg-[linear-gradient(to_top,oklch(var(--sidebar)/0.9)_10%,oklch(var(--sidebar)/0.35)_55%,transparent_80%)] lg:block" />
      <div className="relative hidden min-w-0 flex-col justify-between px-10 py-10 lg:flex xl:px-16 xl:py-14">
        <Wordmark intro className="text-[30px]" />
        <div className="max-w-[620px]">
          <RevealText
            as="p"
            text="Чаты, файлы и проекты — в одном рабочем пространстве."
            delay={500}
            className="font-display text-[clamp(2.75rem,4.2vw,4.5rem)] leading-[1.08] tracking-[-0.035em] [overflow-wrap:anywhere]"
          />
          <p
            className="mc-enter mt-6 max-w-[48ch] text-[16px] leading-relaxed text-muted-foreground"
            style={{ animationDelay: '1100ms' }}
          >
            Начните новый разговор или вернитесь к сохранённой работе. Данные профилей изолированы
            на сервере.
          </p>
        </div>
        <span className="font-mono text-[12px] text-muted-foreground">Notion · Maclaren</span>
      </div>

      <div className="relative flex min-h-full items-center justify-center px-4 py-10 md:px-8 lg:bg-background lg:py-16">
        <div
          className={cn(
            'w-full max-w-[420px] transition-[opacity,transform] duration-300 ease-[var(--ease-in)]',
            leaving && 'pointer-events-none -translate-y-2 scale-[0.98] opacity-0',
          )}
        >
          <div className="mb-8 flex items-center justify-center lg:hidden">
            <Wordmark intro className="text-[30px]" />
          </div>

          <form
            ref={formRef}
            onSubmit={onSubmit}
            onAnimationEnd={(e) => {
              if (e.target === e.currentTarget) e.currentTarget.classList.remove('mc-shake');
            }}
            className="rounded-2xl border border-border bg-surface/95 p-6 shadow-xl shadow-[oklch(var(--primary)/0.08)] lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none"
          >
            <div key={mode} className="mc-stagger">
              <h1 className="mb-1 text-center text-[22px] font-semibold">{heading}</h1>
              <p className="mb-6 text-center text-[13px] text-muted-foreground">{subcopy}</p>
            </div>

            <div className="mc-stagger space-y-3">
              {/* Sign-in needs the login; bootstrap & request-access need a real name. */}
              {!bootstrap && mode === 'login' ? (
                <label className="block">
                  <span className="mb-1.5 block text-[13px] font-medium">Логин</span>
                  <input
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      setError(null);
                    }}
                    aria-invalid={!!error}
                    aria-describedby={error ? 'login-error' : undefined}
                    className="w-full rounded-lg border border-border-strong bg-background px-3.5 py-2.5 text-[16px] outline-none focus:border-[var(--color-focus)] focus:shadow-[0_0_0_3px_oklch(var(--ring)/0.25)] md:text-[14px]"
                    placeholder="workspace1234"
                  />
                </label>
              ) : (
                <label className="block">
                  <span className="mb-1.5 block text-[13px] font-medium">Имя</span>
                  <input
                    type="text"
                    autoComplete="name"
                    required
                    value={realName}
                    onChange={(e) => {
                      setRealName(e.target.value);
                      setError(null);
                    }}
                    className="w-full rounded-lg border border-border-strong bg-background px-3.5 py-2.5 text-[16px] outline-none focus:border-[var(--color-focus)] focus:shadow-[0_0_0_3px_oklch(var(--ring)/0.25)] md:text-[14px]"
                    placeholder="Ваше имя"
                  />
                </label>
              )}
              <label className="block">
                <span className="mb-1.5 block text-[13px] font-medium">Пароль</span>
                <input
                  type="password"
                  autoComplete={bootstrap || isRegister ? 'new-password' : 'current-password'}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError(null);
                  }}
                  aria-invalid={!!error}
                  aria-describedby={error ? 'login-error' : undefined}
                  className="w-full rounded-lg border border-border-strong bg-background px-3.5 py-2.5 text-[16px] outline-none focus:border-[var(--color-focus)] focus:shadow-[0_0_0_3px_oklch(var(--ring)/0.25)] md:text-[14px]"
                  placeholder={bootstrap || isRegister ? 'минимум 8 символов' : '••••••••'}
                />
              </label>

              <div className="min-h-[1lh]">
                {error && (
                  <p id="login-error" className="text-[13px] text-destructive" role="alert">
                    {error}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                aria-busy={submitting || undefined}
                className="mc-press inline-flex min-h-11 w-full items-center justify-center rounded-lg bg-primary py-2.5 font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" aria-label="Отправка" />
                ) : bootstrap ? (
                  'Создать аккаунт'
                ) : isRegister ? (
                  'Отправить заявку'
                ) : (
                  'Войти'
                )}
              </button>
              {!bootstrap && registrationOpen && (
                <button
                  type="button"
                  onClick={() => {
                    setMode(mode === 'login' ? 'register' : 'login');
                    setError(null);
                    setPassword('');
                  }}
                  className="min-h-11 w-full text-center text-[13px] text-muted-foreground hover:text-foreground"
                >
                  {mode === 'login' ? 'Нет доступа? Запросить' : 'Уже есть логин? Войти'}
                </button>
              )}
            </div>
          </form>

          <p
            className="mc-enter mt-6 text-center text-[12px] leading-relaxed text-muted-foreground"
            style={{ animationDelay: '400ms' }}
          >
            На сообщения отвечает агент Notion. Чаты, файлы и проекты каждого профиля изолированы на
            сервере.
          </p>
        </div>
      </div>
    </div>
  );
}
